import { formatarInteiro } from '@/lib/formato';

import type { OpcaoDeConfiguracao } from './frota';

/**
 * O QUE O CADASTRO DA MÁQUINA JÁ SABE PELA CONFIGURAÇÃO (pente fino de
 * 31/10/2026: "o modelo escolhido preenche fabricante, tipo, combustível e
 * capacidade quando o cadastro do modelo tem").
 *
 * A configuração é o nível que a máquina HERDA (medidor, capacidade do tanque,
 * motor, migração 0006): em branco na máquina quer dizer "igual à
 * configuração". Por isso a tela não copia esses valores para os campos — copiar
 * faria a máquina deixar de herdar, e a correção feita na configuração não
 * chegaria nela. A tela os MOSTRA, em etiqueta, e os campos só abrem para quem
 * precisa sobrescrever.
 *
 * A DESCRIÇÃO é diferente: é da máquina, e não se herda. Ela vem sugerida pelo
 * tipo, fabricante e modelo ("Escavadeira Komatsu PC200-8"), com a marca de
 * pré-preenchido, e a pessoa completa ou troca.
 *
 * Puro, para o teste provar as frases sem desenhar a tela.
 */

export type Medidor = 'horimetro' | 'odometro';

function medidorDe(valor: string | null | undefined): Medidor | null {
  return valor === 'horimetro' || valor === 'odometro' ? valor : null;
}

/** O medidor que vale: o escolhido na máquina, ou o da configuração. */
export function medidorEfetivo(escolhido: string, configuracao: OpcaoDeConfiguracao | null): Medidor | null {
  return medidorDe(escolhido) ?? medidorDe(configuracao?.tipoMedidor);
}

/** O rótulo da leitura inicial, pelo medidor que vale. */
export function rotuloDaLeituraInicial(medidor: Medidor | null): string {
  return medidor === 'odometro' ? 'Odômetro inicial' : medidor === 'horimetro' ? 'Horímetro inicial' : 'Leitura inicial do medidor';
}

/** A descrição sugerida, ou `null` quando não há configuração de onde tirá-la. */
export function descricaoSugerida(tipo: string, configuracao: OpcaoDeConfiguracao | null): string | null {
  if (configuracao === null) return null;
  const partes = [tipo.trim() || configuracao.tipo?.trim() || '', configuracao.fabricante?.trim() ?? '', configuracao.modelo?.trim() ?? '']
    .filter((parte) => parte !== '');
  return partes.length === 0 ? null : partes.join(' ');
}

export interface EtiquetaHerdada {
  readonly rotulo: string;
  readonly valor: string;
  /** O valor é o da configuração (herdado), e não o que a pessoa escreveu. */
  readonly herdado: boolean;
  /** Nem a máquina nem a configuração dizem: a etiqueta fica no apagado. */
  readonly vazio: boolean;
}

/**
 * As três etiquetas da linha "Da configuração": o que vale para a máquina, e
 * de onde veio. O escrito na máquina vence o herdado — é a mesma regra do
 * `coalesce(unidade, configuração)` do banco.
 */
export function etiquetasHerdadas(
  escrito: { readonly medidor: string; readonly capacidade: string; readonly motor: string },
  configuracao: OpcaoDeConfiguracao | null,
): readonly EtiquetaHerdada[] {
  const nomeDoMedidor = (m: Medidor | null) => (m === 'odometro' ? 'Odômetro' : m === 'horimetro' ? 'Horímetro' : null);
  const medidorEscrito = nomeDoMedidor(medidorDe(escrito.medidor));
  const medidorHerdado = nomeDoMedidor(medidorDe(configuracao?.tipoMedidor));
  const capacidadeHerdada =
    configuracao?.capacidadeTanqueL === null || configuracao?.capacidadeTanqueL === undefined
      ? null
      : `${formatarInteiro(configuracao.capacidadeTanqueL)} L`;
  const capacidadeEscrita = escrito.capacidade.trim() === '' ? null : `${escrito.capacidade.trim()} L`;
  const motorEscrito = escrito.motor.trim() === '' ? null : escrito.motor.trim();
  const motorHerdado = configuracao?.motorModelo?.trim() ? configuracao.motorModelo.trim() : null;

  const etiqueta = (rotulo: string, propria: string | null, herdada: string | null, vazio: string): EtiquetaHerdada =>
    propria !== null
      ? { rotulo, valor: propria, herdado: false, vazio: false }
      : herdada !== null
        ? { rotulo, valor: herdada, herdado: true, vazio: false }
        : { rotulo, valor: vazio, herdado: false, vazio: true };

  return [
    etiqueta('Medidor', medidorEscrito, medidorHerdado, 'Não informado'),
    etiqueta('Tanque', capacidadeEscrita, capacidadeHerdada, 'Não informado'),
    etiqueta('Motor', motorEscrito, motorHerdado, 'Não informado'),
  ];
}
